import React, { useMemo, useRef } from "react";
import { MapContainer, GeoJSON } from "react-leaflet";
import L from "leaflet";
import "./DocMap.css";

const centerIndia = [22.5, 82];

// 🔹 Convert value to color scale (blue shades for doc handling)
function valueToColor(p) {
  const clamp = Math.max(0, Math.min(1, Number.isFinite(p) ? p : 0));
  const r = Math.round(219 + (37 - 219) * clamp);   // light → dark blue
  const g = Math.round(234 + (99 - 234) * clamp);
  const b = Math.round(254 + (235 - 254) * clamp);
  return `rgb(${r},${g},${b})`;
}

export default function DocMap({ geo, metrics, metric }) {
  const geoRef = useRef(null);

  const styleFn = (feature) => {
    const name = feature.properties?.st_nm || feature.properties?.ST_NM;
    const val = metrics?.[name]?.[metric] ?? 0;
    return {
      fillColor: valueToColor(val),
      color: "#1e3a8a", // dark blue border
      weight: 1,
      fillOpacity: 0.85,
    };
  };

  const onEachFeature = (feature, layer) => {
    const name = feature.properties?.st_nm || feature.properties?.ST_NM;
    const data = metrics?.[name] || {};

    const pct = (v) =>
      v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`;

    let html = `
      <div class="doc-tooltip">
        <div><b>${name}</b></div>
        <div>${metric}: <b>${pct(data[metric])}</b></div>
      `;

    // Show all available methods in tooltip
    if (data && Object.keys(data).length > 1) {
      html += `<div><b>All Methods:</b></div>`;
      for (const [method, val] of Object.entries(data)) {
        html += `<div>${method}: ${pct(val)}</div>`;
      }
    }

    html += `</div>`;

    layer.bindTooltip(html, {
      sticky: true,
      direction: "top",
      opacity: 0.95,
      className: "custom-tooltip",
    });
  };

  const bounds = useMemo(() => {
    try {
      const gj = L.geoJSON(geo);
      return gj.getBounds();
    } catch {
      return null;
    }
  }, [geo]);

  return (
    <MapContainer
      className="map-container"
      center={centerIndia}
      zoom={5}
      scrollWheelZoom={false}
      bounds={bounds || undefined}
      attributionControl={false}
    >
      <GeoJSON
        ref={geoRef}
        data={geo}
        style={styleFn}
        onEachFeature={onEachFeature}
      />

      {/* 🔹 Legend */}
      <div className="legend">
        <div>{metric} (%)</div>
        <div
          className="legend-bar"
          style={{
            background: "linear-gradient(to right, #dbeafe, #1e40af)",
          }}
        ></div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>0%</span>
          <span>100%</span>
        </div>
      </div>
    </MapContainer>
  );
}
